// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import { Form, Formik } from 'formik';
import React from 'react';
import { Box, Flex } from '~/design-system';
import ImportOptionsSkeleton from '../ImportOptionsSkeleton';
import ReadyCheckboxField from '../ReadyCheckboxField';

/**
 * The props that this component needs.
 * @typedef {object} ReadyStepProps
 * @property {object} importData
 * @property {string} updateOptions
 */

/**
 * Renders the ready to import page with the import statistics
 * The view. The props are passed in from useReady - all the logic resides there.
 * @param {{setFormikContext: function, onSubmit: function} & ReturnType<useReady>} props
 * @returns
 */
export default function ReadyToImport({
  importData = {},
  loading,
  setFormikContext,
  onSubmit,
}) {
  if (loading)
    return (
      <Flex sx={{ flexDirection: 'column', padding: '0px 10% 30px 10%' }}>
        <ImportOptionsSkeleton />
      </Flex>
    );
  const { preview: previewStatistics } = importData;
  return (
    <Flex sx={{ flexDirection: 'column', padding: '0px 15% 30px 15%' }}>
      <ImportOptions
        statistics={previewStatistics}
        setFormikContext={setFormikContext}
        onSubmit={onSubmit}
      />
    </Flex>
  );
}

function ImportOptions({ statistics, setFormikContext, onSubmit }) {
  const aliasStatistics = statistics?.find((stat) => stat.object === 'ALIAS');
  const partnerStatistics = statistics?.find(
    (stat) => stat.object === 'PARTNER'
  );
  const initialValues = {
    addedCount: aliasStatistics?.addedCount > 0,
    updatedCount: partnerStatistics?.updatedCount > 0,
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(props) => {
        // Map to import-specific flags
        const importOptions = {
          updateRequests: props.updated,
        };
        onSubmit(importOptions);
      }}
    >
      {(formikContext) => {
        setFormikContext(formikContext);
        return (
          <Form>
            <Box>
              <Box sx={{ margin: 'auto', width: '330px', textAlign: 'left' }}>
                <ReadyCheckboxField
                  name="addedCount"
                  showCheckbox={false}
                  count={aliasStatistics?.addedCount}
                >
                  <Trans>Aliases will be added.</Trans>
                </ReadyCheckboxField>
                <ReadyCheckboxField
                  name="addedCount"
                  showCheckbox={false}
                  count={aliasStatistics?.updatedCount}
                >
                  <Trans>Aliases will be updated.</Trans>
                </ReadyCheckboxField>
                <ReadyCheckboxField
                  name="addedCount"
                  showCheckbox={false}
                  count={aliasStatistics?.unchangedCount}
                >
                  <Trans>Aliases already exist and will not be changed.</Trans>
                </ReadyCheckboxField>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

export function useReady({ workspaceId, importId }) {
  const { data, error, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });
  const [importMasterList] = useMutation(IMPORT_ALIAS);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  return {
    importId,
    ...afterQuery(data),
    error,
    loading,
    isSubmitting,
    onSubmit: async (importOptions) => {
      setIsSubmitting(true);
      const variables = {
        importId,
        importOptions,
      };
      await importMasterList({ variables });
      setIsSubmitting(false);
    },
  };
}

function afterQuery(data) {
  if (!data) return {};
  return { importData: data?.workspace?.dataImport };
}

const QUERY = gql`
  query ReadyPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        importOptions
        preview {
          object
          addedCount
          updatedCount
          unchangedCount
        }
      }
    }
  }
`;

export const IMPORT_ALIAS = gql`
  mutation finalizePartnerAliasImport(
    $importId: ID!
    $importOptions: AliasImportOptionsInput!
  ) {
    result: finalizePartnerAliasImport(
      input: { importId: $importId, importOptions: $importOptions }
    ) {
      code
      success
      message
    }
  }
`;
